import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import {
  AIQuestion,
  BrandPlanSchedule,
  BrandPlanSchedulePlan,
  CampaignContentPreferences,
  CrosspostStrategy,
  EvergreenStrategy,
  Source,
  Theme,
} from 'types'
import { buildErrorMessage } from 'utils'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { reportError } from 'utils/error'
import { UseMutationOptions } from 'react-query/types/react/types'
import { CrosspostStrategyInput } from 'hooks/queries/useUpdateBrandPlan'
import { scheduleKeys } from './scheduleKeys'
import { IMediaFile } from './useCampaignMediaFiles'

interface BrandPlanScheduleVariables {
  goal: string
  audience: string
  cover_image: string
  reviewed: boolean
  questions: AIQuestion[]
  theme: string
  themes: Theme[]
  call_to_action: string
  target_link: string
}

export interface Variables extends Partial<BrandPlanSchedulePlan & BrandPlanScheduleVariables> {
  strategy?: Partial<EvergreenStrategy | CrosspostStrategyInput>
  update_all?: boolean
  sources?: Array<Source & { _destroy?: boolean }>
  media_files?: Array<IMediaFile & { _destroy?: boolean }>
  length?: number
  content_preferences?: CampaignContentPreferences
}

interface Props {
  id: number
}

interface Data {
  schedule: BrandPlanSchedule
}

export const updateBrandPlanSchedule = async ({
  id,
  workspaceId,
  ...variables
}: Props & Variables & { workspaceId: number }) => {
  const response = await sendToApi<Data>(`/w/${workspaceId}/schedules/${id}`, {
    method: 'PUT',
    body: { ...variables },
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    reportError('Failed to update schedule', {
      metaData: {
        id,
        variables,
        response: response.data,
      },
    })
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }
  return response.data
}

export interface Context {
  previousSchedule?: BrandPlanSchedule
  newSchedule?: BrandPlanSchedule
}

export interface Options
  extends Omit<UseMutationOptions<Data, Error, Variables, Context>, 'mutationKey' | 'mutationFn'> {}

export const useUpdateBrandPlanSchedule = ({ id }: Props, options?: Options) => {
  const currentWorkspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<Data, Error, Variables, Context>(
    ['schedules', 'detail', id],
    (variables) => {
      return updateBrandPlanSchedule({ id, workspaceId: currentWorkspaceId, ...variables })
    },
    {
      onMutate: async (variables) => {
        await queryClient.cancelQueries({ queryKey: scheduleKeys.detail(id) })

        const previousSchedule = queryClient.getQueryData<BrandPlanSchedule>(scheduleKeys.detail(id))

        if (!previousSchedule) {
          return undefined
        }

        const { theme: themeId, themes, strategy, update_all, sources, media_files, ...rest } = variables

        const newSchedule = {
          ...previousSchedule,
          ...rest,
          theme: (themes && themeId ? themes.find((t) => t.id === themeId) : undefined) ?? previousSchedule.theme,
          themes: themes ?? previousSchedule.themes,
        }

        if (strategy && ('call_to_action' in strategy || 'target_link' in strategy)) {
          const crosspostStrategy = strategy as CrosspostStrategyInput
          const previousStrategy = previousSchedule.strategy as CrosspostStrategy
          newSchedule.strategy = {
            ...previousStrategy,
            call_to_action: crosspostStrategy.call_to_action || previousStrategy.call_to_action,
            target_link: crosspostStrategy.target_link || previousStrategy.target_link,
          }
        }

        queryClient.setQueryData(scheduleKeys.detail(id), newSchedule)

        return { previousSchedule, newSchedule }
      },
      onError: (_err, _variables, context) => {
        if (context?.previousSchedule) {
          queryClient.setQueryData(scheduleKeys.detail(id), context.previousSchedule)
        }
      },
      onSettled: (data) => {
        if (data) {
          queryClient.invalidateQueries(scheduleKeys.detail(id))
          queryClient.invalidateQueries([data.schedule.brand_plan_id, 'schedules', 'list'])
          queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'schedules', 'list'])
        }
      },
      ...options,
    },
  )
}
